import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import {resource} from '@albert-io/json-api-framework/request/builder';
import sessionStore from 'client/Session/SessionStore';
import {List} from 'immutable';
import type {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import type {SubjectModelV2} from 'resources/augmented/Subject/SubjectModel.v2';

function getClassroomsByUserIdQuery(userId: string, isStudent: boolean): QueryBuilder {
  return resource(isStudent ? 'student_classrooms_v1' : 'classrooms_v1')
    .mandarkEndpoint([
      isStudent ? 'students_v2' : 'teachers_v1',
      userId,
      isStudent ? 'student_classrooms_v1' : 'classrooms_v1'
    ])
    .include('subjects_v2');
}

function getGuessesByUserIdAndGuideLevelIdQuery(userId: string, guideLevelId: string) {
  return resource('guesses_v1')
    .mandarkEndpoint(['students_v2', userId, 'guesses_v1'])
    .include('question_v3.question_set_v1.guide_levels_v2')
    .filter({
      question_v3: {
        question_set_v1: {
          guide_levels_v2: {
            id: guideLevelId
          }
        }
      }
    })
    .meta({
      context: {
        guess: {
          most_recent: true
        }
      }
    })
    .filter({
      'meta.is_most_recent_guess': true
    });
}

function getGuideBySubjectIdQuery(subjectId: string, guideType: string): QueryBuilder {
  return resource('guide_v1')
    .mandarkEndpoint(['guides_v1'])
    .findOne()
    .include('subject_v2')
    .include('guide_levels_v2.subject_v2')
    .filter({
      guide_type: guideType,
      subject_v2: subjectId
    })
    .meta(
      {
        context: {
          guess: {
            most_recent: true
          },
          student: {
            id: sessionStore.getUserId()
          }
        }
      },
      sessionStore.hasValidSession() && sessionStore.isStudent()
    )
    .customQuery({
      with_meta: 'guide_v1,guide_level_v2'
    });
}

function isPartialCreditEnabledForSubject(
  subject: SubjectModelV2,
  classrooms: List<ClassroomModelV1>,
  isStudent: boolean
) {
  if (!classrooms) return false;
  const matchingClassroomSubjects = classrooms
    .reduce(
      //@ts-expect-error
      (allSubjects, classroom) => allSubjects.concat(classroom?.get('subjects')),
      List<SubjectModelV2>()
    )
    .filter((classroomSubject) => classroomSubject.getId() === subject.getId());

  return matchingClassroomSubjects.some(
    (matchingClassroomSubject) =>
      matchingClassroomSubject
        .get('relationshipMeta')
        ?.get(isStudent ? 'student_classroom' : 'classroom')
        .toIndexedSeq()
        .first()
        .get('partial_credit_enabled') ?? false
  );
}

export default {
  getClassroomsByUserIdQuery,
  getGuessesByUserIdAndGuideLevelIdQuery,
  getGuideBySubjectIdQuery,
  isPartialCreditEnabledForSubject
};
